import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminAPI } from '../config/api';
import AdminSidebar from '../components/AdminSidebar';
import { showAppToast } from '../utils/notifications';

const emptyUserForm = {
  firstName: '',
  lastName: '',
  email: '',
  role: 'user',
  password: '',
};

const roleChoices = [
  { value: 'user', label: 'Customer' },
  { value: 'admin', label: 'Super Admin' },
  { value: 'fleet_manager', label: 'Fleet Manager' },
  { value: 'reservations_manager', label: 'Reservations Manager' },
  { value: 'support_agent', label: 'Support Agent' },
  { value: 'account_manager', label: 'Account Manager' },
];

function userToForm(user) {
  return {
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    role: (user?.role || 'user').toLowerCase(),
    password: '',
  };
}

function getUserName(user) {
  return `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email || 'Selected user';
}

function AdminUserForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState(emptyUserForm);

  useEffect(() => {
    if (!isEditing) return;

    let isMounted = true;
    const fetchUser = async () => {
      setLoading(true);
      try {
        const response = await adminAPI.getUser(id);
        if (!isMounted) return;
        const user = response.data;
        setSelectedUser(user);
        setFormData(userToForm(user));
      } catch (error) {
        showAppToast({ message: error.response?.data?.message || 'Failed to load selected user', type: 'error' });
        navigate('/admin/users', { replace: true });
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchUser();
    return () => {
      isMounted = false;
    };
  }, [id, isEditing, navigate]);

  const pageCopy = useMemo(() => {
    if (isEditing) {
      return {
        eyebrow: 'Edit User',
        title: 'Update account access',
        description: `Adjust privileges, identity, and optional password reset for ${getUserName(selectedUser)}.`,
        submit: 'Save User',
      };
    }

    return {
      eyebrow: 'Create User',
      title: 'Add a verified dashboard account',
      description: 'Create a new account and assign the correct access level before the user signs in.',
      submit: 'Create User',
    };
  }, [isEditing, selectedUser]);

  const handleFormChange = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const submitUser = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = { ...formData };
      if (isEditing && !payload.password) {
        delete payload.password;
      }

      if (isEditing) {
        await adminAPI.updateUser(id, payload);
        showAppToast({ message: 'User updated successfully', type: 'success', duration: 2400 });
      } else {
        await adminAPI.createUser(payload);
        showAppToast({ message: 'User created successfully', type: 'success', duration: 2400 });
      }

      navigate('/admin/users');
    } catch (error) {
      showAppToast({ message: error.response?.data?.message || 'Failed to save user', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-layout">
        <AdminSidebar />
        <div className="admin-content px-4 py-16">
          <div className="admin-route-loading-card card-luxury" role="status" aria-live="polite">
            <i className="bi bi-person-gear" aria-hidden="true" />
            <strong>Loading user</strong>
            <p className="text-secondary-theme">Opening the selected account editor...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content px-4 py-8">
        <div className="mx-auto max-w-5xl">
          <section className="admin-header admin-editor-hero card-luxury">
            <div>
              <p className="text-accent">{pageCopy.eyebrow}</p>
              <h1 className="section-title text-4xl text-primary-theme">{pageCopy.title}</h1>
              <p className="text-secondary-theme">{pageCopy.description}</p>
            </div>
            <button type="button" className="btn-luxury-outline" onClick={() => navigate('/admin/users')}>
              <i className="bi bi-arrow-left" aria-hidden="true" />
              Back to Users
            </button>
          </section>

          <form className="admin-form-card card-luxury admin-user-form" onSubmit={submitUser} aria-label="Admin user form">
            <div className="admin-form-grid">
              <label className="admin-field">
                <span><i className="bi bi-person" aria-hidden="true" /> First name</span>
                <input value={formData.firstName} onChange={(event) => handleFormChange('firstName', event.target.value)} required />
              </label>
              <label className="admin-field">
                <span><i className="bi bi-person-badge" aria-hidden="true" /> Last name</span>
                <input value={formData.lastName} onChange={(event) => handleFormChange('lastName', event.target.value)} required />
              </label>
              <label className="admin-field">
                <span><i className="bi bi-envelope" aria-hidden="true" /> Email</span>
                <input type="email" value={formData.email} onChange={(event) => handleFormChange('email', event.target.value)} required />
              </label>
              <label className="admin-field">
                <span><i className="bi bi-shield-check" aria-hidden="true" /> Role</span>
                <select value={formData.role} onChange={(event) => handleFormChange('role', event.target.value)}>
                  {roleChoices.map((choice) => (
                    <option key={choice.value} value={choice.value}>{choice.label}</option>
                  ))}
                </select>
              </label>
              <label className="admin-field">
                <span><i className="bi bi-key" aria-hidden="true" /> {isEditing ? 'New password (optional)' : 'Password'}</span>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(event) => handleFormChange('password', event.target.value)}
                  required={!isEditing}
                  minLength={8}
                />
              </label>
            </div>
            <div className="admin-editor-actions">
              <button type="submit" className="btn-luxury admin-form-submit" disabled={saving}>
                <i className="bi bi-check2-circle" aria-hidden="true" />
                {saving ? 'Saving...' : pageCopy.submit}
              </button>
              <button type="button" className="btn-luxury-outline" onClick={() => navigate('/admin/users')} disabled={saving}>
                <i className="bi bi-x-lg" aria-hidden="true" />
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AdminUserForm;
